type RGB = [number, number, number];

/**
 * Convert a hex color string to an RGB tuple.
 * @param hex - Color in #RRGGBB format
 * @returns [r, g, b], each 0-255
 */
export function hexToRgb(hex: string): RGB {
  const match = hex
    .replace(/^#/, '')
    .match(/([A-Fa-f0-9]{2})([A-Fa-f0-9]{2})([A-Fa-f0-9]{2})/);
  if (!match) throw new Error(`Invalid hex color: ${hex}`);
  const [, rs, gs, bs] = match;
  return [parseInt(rs, 16), parseInt(gs, 16), parseInt(bs, 16)];
}

/**
 * Convert an RGB tuple to a hex color string.
 * @param rgb - [r, g, b], each 0-255
 * @returns Color in #RRGGBB format
 */
export function rgbToHex([r, g, b]: RGB): string {
  const toHex = (v: number) => {
    const clamped = Math.max(0, Math.min(255, Math.round(v)));
    return clamped.toString(16).padStart(2, '0');
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Convert an sRGB channel (0-255) to linear light (0–1).
 * More info about the magic numbers: https://en.wikipedia.org/wiki/SRGB#Reverse_transformation
 * @param channel - 0-255
 */
function sRGBtoLinear(channel: number): number {
  const v = channel / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

/**
 * Convert a linear light channel (0–1) back to sRGB (0-255).
 * More info about the magic numbers: https://en.wikipedia.org/wiki/SRGB#Reverse_transformation
 * @param linear - 0–1
 */
function linearToSRGB(linear: number): number {
  const v =
    linear <= 0.0031308 ? linear * 12.92 : 1.055 * Math.pow(linear, 1 / 2.4) - 0.055;
  return Math.round(v * 255);
}

/**
 * Mixes an array of hex colors by given weights, using linear RGB for accuracy.
 * @param baseHexes - Array of colors in #RRGGBB format
 * @param weights - Array of weights. If they do not sum to 1, they will be normalized.
 * @returns Mixed color in #RRGGBB format
 */
export function mixLinearRGB(baseHexes: string[], weights: number[]): string {
  if (baseHexes.length !== weights.length) {
    throw new Error('baseHexes and weights must have the same length');
  }

  // Normalize weights if they don't sum to 1
  const total = weights.reduce((sum, w) => sum + w, 0);
  const norm = weights.map(w => w / total);

  // Convert all hex to linear RGB
  const linearColors: [number, number, number][] = baseHexes.map(hex => {
    const [r, g, b] = hexToRgb(hex);
    return [sRGBtoLinear(r), sRGBtoLinear(g), sRGBtoLinear(b)];
  });

  // Weighted sum
  const mixedLinear: [number, number, number] = [0, 0, 0];
  for (let i = 0; i < linearColors.length; i++) {
    mixedLinear[0] += linearColors[i][0] * norm[i];
    mixedLinear[1] += linearColors[i][1] * norm[i];
    mixedLinear[2] += linearColors[i][2] * norm[i];
  }

  // Convert back to sRGB and hex
  const mixedSRGB: RGB = [
    linearToSRGB(mixedLinear[0]),
    linearToSRGB(mixedLinear[1]),
    linearToSRGB(mixedLinear[2]),
  ];

  return rgbToHex(mixedSRGB);
}
